import React from 'react'
import { observer } from 'mobx-react-lite'

import { DateRange } from '@mui/x-date-pickers-pro'
import { Icon } from '@mxenabled/mxui'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'

import { addSeconds } from 'date-fns/addSeconds'
import { differenceInCalendarMonths } from 'date-fns/differenceInCalendarMonths'

import {
  ANALYTICS_EVENTS,
  Loader,
  WidgetContainer,
  useAccountStore,
  useEvent,
  useCategoryStore,
  useGlobalCopyStore,
  useGlobalUiStore,
  useScreenSize,
  useTransactionStore,
  useTrendsStore,
  useWidgetLoadTimer,
} from '../common'
import { WidgetProps } from '../core'
import { StackedLineChart } from '../common/components/charts/StackedLineChart'

import { getStackedLineChartData, StackedLineChartData } from '../core/utils/TrendsUtil'
import { getTopLevelCategoryTotals, isExpense, isIncome } from './utils/shared'

import TrendsViewToggle, { type TrendsTabValue } from './components/TrendsViewToggle'
import DateRangeText from './components/DateRangeText'
import TrendsTableContainer from './components/TrendsTableContainer'
import TrendsDrawer from './components/TrendsDrawer'
import { TRENDS_ANALYTICS_METADATA } from './constants/TrendsWidget'

const TrendsFullWidget: React.FC<WidgetProps> = ({ onBackClick, onMenuClick, sx }) => {
  const { onEvent } = useEvent()
  const { isDesktop, isTablet, isMobile } = useScreenSize()
  const { isAccountDataLoaded, loadAccountData } = useAccountStore()
  const {
    categoriesLoaded,
    loadCategories,
    monthlyCategoryTotals,
    getCategoryName,
    reloadCategoryTotals,
  } = useCategoryStore()
  const {
    isTransactionDataLoaded,
    loadTransactionData,
    sortedTransactions: transactions,
  } = useTransactionStore()
  const { selectedDateRange, setSelectedDateRange } = useTrendsStore()
  const { isInitialized, selectedAccounts, selectedAccountGuids } = useGlobalUiStore()
  const { trends: copy } = useGlobalCopyStore()

  const [isWidgetInitialized, setIsWidgetInitialized] = React.useState(false)
  const [showTransactions, setShowTransactions] = React.useState(false)
  const [selectedTab, setSelectedTab] = React.useState<TrendsTabValue>('Chart')
  const [selectedCategory, setSelectedCategory] = React.useState('')

  // Control responsive chart height based on available widget iframe viewport height
  const [viewportHeight, setViewportHeight] = React.useState(window.innerHeight)
  const unavailableChartHeight = isMobile ? 315 : 345
  const availableChartHeight = viewportHeight - unavailableChartHeight

  useWidgetLoadTimer({
    widgetName: 'TrendsFullWidget',
    isLoaded: isWidgetInitialized,
  })

  React.useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight)
    window.addEventListener('resize', handleResize)

    if (!isAccountDataLoaded) {
      loadAccountData().finally()
    }
    if (!categoriesLoaded) {
      loadCategories().finally()
    }
    if (!isTransactionDataLoaded) {
      loadTransactionData().finally()
    }

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Initialize monthlyCategoryTotals, and reload on account or date range change.
  // Depends on primitives rather than `selectedAccounts` / `selectedDateRange`,
  // whose object identities change even when the values do not. (MEW-3910)
  //
  // Loads both totals in parallel; see TrendsWidget for why chaining the monthly
  // request behind the date-range one reintroduces the stale-write race.
  // Also waits for account data so the first request isn't fired with an empty
  // account list and immediately superseded. (MEW-3910)
  React.useEffect(() => {
    if (isInitialized && isAccountDataLoaded) {
      reloadCategoryTotals(
        selectedAccounts,
        selectedDateRange.start,
        selectedDateRange.end,
      ).finally(() => {
        setIsWidgetInitialized(true)
        onEvent(ANALYTICS_EVENTS.TRENDS_LOAD_WIDGET, {
          ...TRENDS_ANALYTICS_METADATA,
          time_period: `${differenceInCalendarMonths(selectedDateRange.end, selectedDateRange.start)}M`,
        })
      })
    }
  }, [
    isInitialized,
    isAccountDataLoaded,
    selectedAccountGuids.join(','),
    selectedDateRange.start.getTime(),
    selectedDateRange.end.getTime(),
  ])

  // Cache a full year of StackedLineChart data
  const chartData = React.useMemo(() => {
    if (!isWidgetInitialized) return { stackedDatasets: [], unstackedDatasets: [] }

    const data = getStackedLineChartData(
      monthlyCategoryTotals,
      selectedDateRange.start,
      selectedDateRange.end,
    )

    const filteredData = selectedCategory.length
      ? data.filter(
          (d) =>
            d.top_level_category_guid === selectedCategory || d.category_guid === selectedCategory,
        )
      : getTopLevelCategoryTotals(data)

    return {
      stackedDatasets: filteredData.filter(isExpense) as StackedLineChartData[],
      unstackedDatasets: filteredData.filter(isIncome) as StackedLineChartData[],
    }
  }, [monthlyCategoryTotals, selectedCategory, selectedDateRange])

  const onTabChange = (_event: React.SyntheticEvent, newValue: TrendsTabValue) => {
    setSelectedTab(newValue ?? selectedTab)
    onEvent(ANALYTICS_EVENTS.TRENDS_CLICK_TOGGLE_VIEW)
  }

  const onRangeChanged = (dateRange: DateRange<Date>) => {
    const startDate = addSeconds(dateRange?.[0] as Date, 1)
    const endDate = dateRange?.[1] as Date
    const months = differenceInCalendarMonths(endDate, startDate)

    setSelectedDateRange({ start: startDate, end: endDate })
    onEvent(ANALYTICS_EVENTS.TRENDS_CLICK_TIME_WINDOW, {
      time_period: months + 'M',
    })
  }

  const onAccountsFilterClick = () => {
    onEvent(ANALYTICS_EVENTS.TRENDS_CLICK_FILTER)
  }
  const onClickRow = (guid: string) => {
    setSelectedCategory(guid)
  }
  const onClickLegend = (guid: string) => {
    setSelectedCategory(guid)
    onEvent(ANALYTICS_EVENTS.TRENDS_CLICK_LEGEND, { category: getCategoryName(guid) })
  }
  const onHoverLegend = (guid: string) => {
    onEvent(ANALYTICS_EVENTS.TRENDS_HOVER_LEGEND, { category: getCategoryName(guid) })
  }
  const onHoverArea = (category: string) => {
    onEvent(ANALYTICS_EVENTS.TRENDS_HOVER_AREA, { category })
  }
  const onHoverPoint = (category: string) => {
    onEvent(ANALYTICS_EVENTS.TRENDS_HOVER_POINT, { category })
  }

  // Mirrors the effect's readiness condition. The effect now also waits for
  // account data, so the render gate must too — otherwise a state where the
  // effect never runs would leave a spinner with nothing to clear it. Matches
  // TrendsWidget's gate. (MEW-3910)
  if (!isInitialized || !isAccountDataLoaded || !isWidgetInitialized) return <Loader />

  return (
    <WidgetContainer
      calendarActions={{ onRangeChanged }}
      dateRange={selectedDateRange}
      dateRangeVariant="timeframebuttons"
      onAccountsFilterClick={onAccountsFilterClick}
      onBackClick={onBackClick}
      onMenuClick={onMenuClick}
      sx={sx}
      title={copy.title}
    >
      <Stack
        sx={{
          // eslint-disable-next-line no-nested-ternary
          px: isDesktop ? 48 : isTablet ? 24 : 12,
        }}
      >
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          sx={{ pb: isMobile ? 12 : 24, pt: isMobile ? 24 : 48 }}
        >
          <DateRangeText
            selectedCategoryGuid={selectedCategory}
            selectedDateRange={selectedDateRange}
          />
          <TrendsViewToggle onTabChange={onTabChange} selectedTab={selectedTab} />
        </Stack>
        <Stack alignItems="center" flexDirection="row" width="100%">
          <Box flexGrow={1}>
            {selectedCategory && (
              <Button onClick={() => setSelectedCategory('')} sx={{ p: 0, pr: 5 }}>
                <Icon name="arrow_back" />
                {copy.all_categories}
              </Button>
            )}
          </Box>
          <Button onClick={() => setShowTransactions(true)} sx={{ px: 5 }}>
            {`${copy.view_transactions} (${transactions.length})`}
          </Button>
        </Stack>
        <Box>
          {selectedTab === 'Chart' && (
            <StackedLineChart
              availableHeight={availableChartHeight}
              onClickLegend={onClickLegend}
              onHoverArea={onHoverArea}
              onHoverLegend={onHoverLegend}
              onHoverPoint={onHoverPoint}
              stackedDatasets={chartData.stackedDatasets}
              unstackedDatasets={chartData.unstackedDatasets}
            />
          )}
          {selectedTab === 'Table' && (
            <TrendsTableContainer
              onClickRow={onClickRow}
              selectedCategory={selectedCategory}
              selectedDateRange={selectedDateRange}
            />
          )}
        </Box>
      </Stack>
      <TrendsDrawer
        categoryGuid={selectedCategory}
        dateRange={selectedDateRange}
        isOpen={showTransactions}
        onClose={() => setShowTransactions(false)}
      />
    </WidgetContainer>
  )
}

export default observer(TrendsFullWidget)
